// src/utils/versionTracker.js (Shell)
//
// Notices when a build this tab is running has been redeployed. Each entry file (the shell's
// index.html, each remote's remoteEntry.js) is fingerprinted by its ETag when the tab first
// loads it; a later HEAD that returns a different ETag means a new build is live. S3 changes
// the ETag only when the content changes.

const baselines = new Map(); // url → Promise<string | null> — the ETag when this tab loaded it
const latest = new Map();    // url → string — the ETag seen on the last check

async function fetchEtag(url) {
    try {
        const res = await fetch(url, { method: 'HEAD', cache: 'no-store' });
        return res.ok ? res.headers.get('etag') : null;
    } catch {
        return null;
    }
}

function isSameOrigin(url) {
    try {
        return new URL(url, window.location.href).origin === window.location.origin;
    } catch {
        return false;
    }
}

/**
 * Record the version of an entry file this tab has loaded. Production builds only, and
 * same-origin only — a cross-origin remote (local runs against the CDN) can't be read.
 * @param {string} url
 */
export function trackVersion(url) {
    if (!import.meta.env.PROD || !isSameOrigin(url) || baselines.has(url)) return;
    baselines.set(url, fetchEtag(url));
}

/**
 * True when any tracked entry file now has a different ETag than the one recorded for it.
 * @returns {Promise<boolean>}
 */
export async function hasNewVersion() {
    const changed = await Promise.all([...baselines].map(async ([url, baselinePromise]) => {
        const baseline = await baselinePromise;
        if (!baseline) {
            // The first HEAD failed (offline, transient error) — take a fresh baseline.
            baselines.set(url, fetchEtag(url));
            return false;
        }
        const current = await fetchEtag(url);
        if (current) latest.set(url, current);
        return Boolean(current) && current !== baseline;
    }));
    return changed.some(Boolean);
}

/**
 * Treat the versions seen on the last check as the current ones, so only a later deploy
 * counts as new. Used when the user cancels the reload.
 */
export function acceptCurrentVersions() {
    for (const [url, etag] of latest) baselines.set(url, Promise.resolve(etag));
}

export function resetVersionTracking() {
    baselines.clear();
    latest.clear();
}
